// src/features/pinFeaturePipeline.js
// Minimal pipeline expected by semanticRanker.js.
// Stores extracted features per pin (images + notes) in editFeaturesStore.
//
// ✅ For images: stores structured JSON from backend (/api/extract-image-features)
// ✅ For notes: stores text
// ❗ embedding is still null for now (you can add embeddings later)

import {
  computePinSourceHash,
  getPinFeatures,
  setPinFeatures,
} from "../state/editFeaturesStore";

import { extractImageFeatures, describeImage } from "../api/openaiProxy";
import { summarisePinFeatures } from "./pinFeatureSummary";

export async function ensurePinFeaturesAndEmbedding(editId, pin) {
  if (!editId || !pin?.id) return null;

  const sourceHash = computePinSourceHash(pin);
  const cached = getPinFeatures(editId, pin.id);

  // If unchanged, return cached
  if (cached?.sourceHash && cached.sourceHash === sourceHash) {
    return cached;
  }

  // Only handle images + notes
  const kind = String(pin?.kind || "").toLowerCase();
  if (kind !== "image" && kind !== "note") return null;

  // ---------- IMAGE ----------
  if (kind === "image") {
    const imageUrl = pin?.imageUrl || pin?.url || pin?.src || null;
    if (!imageUrl) return null;

    // Prefer structured JSON from backend
    let json = null;
    try {
      json = await extractImageFeatures(imageUrl);
    } catch (e) {
      console.warn("[pinFeaturePipeline] extractImageFeatures failed, falling back to caption", e);
    }

    // Optional fallback caption (handy for debugging/UX)
let caption = null;
const hasStructured =
  json && typeof json === "object" && (json.features || json.data || Object.keys(json).length);

if (!hasStructured) {
  try {
    caption = await describeImage(imageUrl);
  } catch (e) {
    console.warn("[pinFeaturePipeline] describeImage fallback failed", e);
  }
}

    // Normalise possible JSON shapes from backend/OpenAI:
    // - could be { garmentType, colors, ... }
    // - could be { features: { ... } }
    // - could be { data: { ... } }
const structured =
  json && typeof json === "object"
    ? (json.features || json.data || json)
    : null;

    // One compact summary string per pin (what we'll later embed + report)
    const summary = summarisePinFeatures({
      ...(caption ? { caption } : {}),
      ...(structured && typeof structured === "object" ? structured : {}),
    });

    const features = {
      ...(caption ? { caption } : {}),
      ...(json ? { image: json } : {}),
      ...(structured ? { structured } : {}),
      ...(summary ? { summary } : {}),
    };

    const entry = {
      kind,
      features,
      embedding: null, // not wired yet
      sourceHash,
    };

    setPinFeatures(editId, pin.id, entry);
    return entry;
  }

  // ---------- NOTE ----------
  if (kind === "note") {
    const text = String(pin?.text || pin?.note || "").trim();
    if (!text) return null;

    const summary = summarisePinFeatures({ text });

    const entry = {
      kind,
      features: { text, ...(summary ? { summary } : {}) },
      embedding: null, // not wired yet
      sourceHash,
    };

    setPinFeatures(editId, pin.id, entry);
    return entry;
  }

  return null;
}

// Optional alias (semanticRanker expects ensurePinFeaturesAndEmbedding, but other files may import ensurePinFeatures)
export async function ensurePinFeatures(editId, pin) {
  return ensurePinFeaturesAndEmbedding(editId, pin);
}